import { useState, useEffect, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useDateLocale } from '@/hooks/useDateLocale';
import { Loader2, Calendar, Clock, MapPin, Fuel, FileBarChart } from 'lucide-react';
import { format, startOfDay, endOfDay, subDays, formatDuration, intervalToDuration } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  fetchDevices,
  fetchSummary,
  type Device,
  type Summary as SummaryType,
} from '@/lib/traccar-api';

const Summary = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const { isAuthenticated } = useTraccarSession();

  const [devices, setDevices] = useState<Device[]>([]);
  const [summaries, setSummaries] = useState<SummaryType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  });

  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const devicesData = await fetchDevices();
      setDevices(devicesData);
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const loadSummary = useCallback(async () => {
    if (!devices.length || !dateRange?.from || !dateRange?.to) return;

    try {
      setIsLoadingSummary(true);
      const from = format(dateRange.from, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      const to = format(dateRange.to, "yyyy-MM-dd'T'HH:mm:ss'Z'");

      const deviceIds = devices.map(d => d.id);
      const summaryData = await fetchSummary(deviceIds, from, to);
      setSummaries(summaryData);
    } catch (error) {
      console.error('Failed to load summary:', error);
      setSummaries([]);
    } finally {
      setIsLoadingSummary(false);
    }
  }, [devices, dateRange]);

  useEffect(() => {
    loadInitialData().then();
  }, [loadInitialData]);

  useEffect(() => {
    if (devices.length > 0 && dateRange?.from && dateRange?.to) {
      loadSummary().then();
    }
  }, [devices, dateRange, loadSummary]);

  const formatHours = (ms: number) => {
    const duration = intervalToDuration({ start: 0, end: ms });
    return formatDuration(duration, { format: ['hours', 'minutes'], zero: false, locale: dateFnsLocale }) || '0h';
  };

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const headerContent = (
    <div className="flex items-center gap-4">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "MMM d", { locale: dateFnsLocale })} - {format(dateRange.to, "MMM d, yyyy", { locale: dateFnsLocale })}
                </>
              ) : (
                format(dateRange.from, "MMM d, yyyy", { locale: dateFnsLocale })
              )
            ) : (
              t('history.selectDates')
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="range"
            selected={dateRange}
            onSelect={setDateRange}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );

  // Calculate totals
  const totals = summaries.reduce(
    (acc, s) => ({
      distance: acc.distance + (s.distance || 0),
      engineHours: acc.engineHours + (s.engineHours || 0),
      spentFuel: acc.spentFuel + (s.spentFuel || 0),
    }),
    { distance: 0, engineHours: 0, spentFuel: 0 }
  );

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.totalDistance')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">{(totals.distance / 1000).toFixed(1)} km</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.engineHours') || 'Engine Hours'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">{formatHours(totals.engineHours)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.fuelUsed') || 'Fuel Used'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Fuel className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">{totals.spentFuel.toFixed(1)} L</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileBarChart className="h-5 w-5" />
              {t('nav.summary')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingSummary ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : summaries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileBarChart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('history.noSummary') || 'No summary data available'}</p>
                <p className="text-sm mt-1">{t('history.noSummaryHint') || 'Try adjusting your date range'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('devices.name') || 'Device'}</TableHead>
                    <TableHead className="text-right">{t('history.distance') || 'Distance'}</TableHead>
                    <TableHead className="text-right">{t('history.avgSpeed') || 'Avg Speed'}</TableHead>
                    <TableHead className="text-right">{t('history.maxSpeed') || 'Max Speed'}</TableHead>
                    <TableHead className="text-right">{t('history.engineHours') || 'Engine Hours'}</TableHead>
                    <TableHead className="text-right">{t('history.fuel') || 'Fuel'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summaries.map((summary) => (
                    <TableRow key={summary.deviceId}>
                      <TableCell className="font-medium">{summary.deviceName}</TableCell>
                      <TableCell className="text-right">{(summary.distance / 1000).toFixed(1)} km</TableCell>
                      <TableCell className="text-right">{Math.round(summary.averageSpeed * 1.852)} km/h</TableCell>
                      <TableCell className="text-right">{Math.round(summary.maxSpeed * 1.852)} km/h</TableCell>
                      <TableCell className="text-right">{formatHours(summary.engineHours)}</TableCell>
                      <TableCell className="text-right">{summary.spentFuel.toFixed(1)} L</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Summary;
